import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  AUTH_MODES,
  getMcpConfiguration,
  mcpError,
  saveMcpConfiguration,
} from "@/api/mcp.js";
import McpCredentials from "./McpCredentials.jsx";
import McpSetup from "./McpSetup.jsx";

/** Copiar una URL sin seleccionarla a mano, con su etiqueta atada al campo. */
const Copiable = ({ id, label, value }) => (
  <Form.Group controlId={id}>
    <Form.Label className="small text-muted">{label}</Form.Label>
    <InputGroup>
      <Form.Control readOnly value={value ?? ""} className="font-monospace" />
      <Button
        variant="outline-secondary"
        onClick={() => navigator.clipboard?.writeText(value ?? "")}
        aria-label="Copiar"
        title="Copiar"
      >
        <i className="bi bi-clipboard" />
      </Button>
    </InputGroup>
  </Form.Group>
);

/**
 * Configuraciones · MCP.
 *
 * Un MCP es la puerta por la que un asistente —Claude, n8n, lo que sea—
 * consulta los datos del despacho. Todo lo que se decide aquí es qué tan
 * abierta está esa puerta: si existe, con qué llave se entra y qué se puede
 * preguntar a través de ella.
 *
 * El interruptor de arriba es el que manda: apagado, el servidor contesta que
 * no está disponible aunque la llave sea buena.
 */
const McpAdmin = () => {
  const [configuration, setConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getMcpConfiguration()
      .then(setConfiguration)
      .catch((error) =>
        Swal.fire(
          "Error",
          mcpError(error, "No se pudo leer la configuración del MCP."),
          "error",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  const update = (changes) =>
    setConfiguration((previous) => ({ ...previous, ...changes }));

  const toggleTool = (name) =>
    update({
      tools: configuration.tools.includes(name)
        ? configuration.tools.filter((tool) => tool !== name)
        : [...configuration.tools, name],
    });

  const save = async () => {
    setSaving(true);
    try {
      setConfiguration(
        await saveMcpConfiguration({
          enabled: configuration.enabled,
          authMode: configuration.authMode,
          tools: configuration.tools,
          oauth: configuration.oauth,
        }),
      );
      Swal.fire(
        "Guardado",
        "La configuración del MCP quedó guardada.",
        "success",
      );
    } catch (error) {
      Swal.fire("No se guardó", mcpError(error), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 p-3">
        <Spinner animation="border" size="sm" />
        <span>Cargando…</span>
      </div>
    );
  }

  if (!configuration) {
    return (
      <Alert variant="danger">No se pudo leer la configuración del MCP.</Alert>
    );
  }

  const oauth = configuration.oauth ?? {};
  const usaLlaves = configuration.authMode === AUTH_MODES.API_KEY;

  return (
    <div className="col">
      <p className="text-muted">
        El MCP deja que un asistente consulte la información del despacho
        —emisores, receptores, facturas y folios— sin entrar a esta pantalla.
        Quien se conecta ve lo mismo que vería el contador al que pertenece la
        llave, y sólo puede consultar: por aquí no se emite ni se cancela nada.
      </p>

      <Card className="mb-3">
        <Card.Header>Servidor</Card.Header>
        <Card.Body>
          <Form.Check
            type="switch"
            id="mcp-enabled"
            className="mb-3"
            checked={configuration.enabled}
            onChange={(event) => update({ enabled: event.target.checked })}
            label={configuration.enabled ? "Encendido" : "Apagado"}
          />
          <Copiable
            id="mcp-endpoint"
            label="Dirección que hay que configurar en el cliente"
            value={configuration.endpoint}
          />
          {!configuration.enabled && (
            <Alert variant="secondary" className="mt-3 mb-0 small">
              Apagado, esa dirección contesta que el servicio no está
              disponible. Las llaves emitidas siguen existiendo y vuelven a
              servir en cuanto lo enciendas.
            </Alert>
          )}
          {configuration.updatedAt && (
            <p className="text-muted small mt-3 mb-0">
              Última vez guardado el{" "}
              {new Date(configuration.updatedAt).toLocaleString("es-MX", {
                dateStyle: "short",
                timeStyle: "short",
              })}
              {configuration.updatedBy ? ` por ${configuration.updatedBy}` : ""}
              .
            </p>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Modo de autenticación</Card.Header>
        <Card.Body>
          <Form.Check
            type="radio"
            id="mcp-mode-key"
            name="mcp-auth-mode"
            checked={usaLlaves}
            onChange={() => update({ authMode: AUTH_MODES.API_KEY })}
            label="Llave de acceso"
          />
          <p className="text-muted small ms-4">
            Nosotros emitimos una llave larga y el cliente la manda en cada
            consulta. Es lo que entiende cualquier cliente MCP y no depende de
            nadie más.
          </p>

          <Form.Check
            type="radio"
            id="mcp-mode-oauth"
            name="mcp-auth-mode"
            checked={!usaLlaves}
            onChange={() => update({ authMode: AUTH_MODES.OAUTH })}
            label="OAuth 2.1"
          />
          <p className="text-muted small ms-4">
            Los tokens los emite el servidor de autorización del despacho y aquí
            sólo se comprueban. Cada persona entra con su propia cuenta y se le
            puede retirar el acceso desde allá, sin tocar esta pantalla.
          </p>

          {!usaLlaves && (
            <Row className="g-3 mt-1">
              <Col md={12}>
                <Form.Group controlId="mcp-oauth-issuer">
                  <Form.Label>Emisor (issuer)</Form.Label>
                  <Form.Control
                    placeholder="https://tu-servidor-oauth/"
                    value={oauth.issuer ?? ""}
                    onChange={(event) =>
                      update({
                        oauth: { ...oauth, issuer: event.target.value },
                      })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="mcp-oauth-audience">
                  <Form.Label>Audiencia (audience)</Form.Label>
                  <Form.Control
                    placeholder={configuration.endpoint}
                    value={oauth.audience ?? ""}
                    onChange={(event) =>
                      update({
                        oauth: { ...oauth, audience: event.target.value },
                      })
                    }
                  />
                  <Form.Text muted>
                    Para quién viene el token. Sin esto, el token que ese mismo
                    emisor le dio a cualquier otro servicio abriría esta puerta.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group controlId="mcp-oauth-jwks">
                  <Form.Label>URL del JWKS</Form.Label>
                  <Form.Control
                    placeholder="https://tu-servidor-oauth/.well-known/jwks.json"
                    value={oauth.jwksUri ?? ""}
                    onChange={(event) =>
                      update({
                        oauth: { ...oauth, jwksUri: event.target.value },
                      })
                    }
                  />
                  <Form.Text muted>
                    De ahí se bajan las llaves públicas con las que se comprueba
                    la firma.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Copiable
                  id="mcp-metadata"
                  label="Lo que el cliente consulta para saber a dónde ir a pedir permiso"
                  value={configuration.metadataUrl}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      <McpSetup configuration={configuration} />

      <Card className="mb-3">
        <Card.Header>Herramientas</Card.Header>
        <Card.Body>
          <p className="text-muted">
            Lo que el asistente puede preguntar. Lo que aquí quede apagado no
            aparece siquiera en su lista: no hay forma de llamarlo aunque se
            sepa el nombre.
          </p>
          {configuration.availableTools.map((tool) => (
            <Form.Check
              key={tool.name}
              type="checkbox"
              id={`mcp-tool-${tool.name}`}
              className="mb-2"
              checked={configuration.tools.includes(tool.name)}
              onChange={() => toggleTool(tool.name)}
              label={
                <>
                  <code>{tool.name}</code>
                  <span className="d-block text-muted small">
                    {tool.description}
                  </span>
                </>
              }
            />
          ))}
          {configuration.tools.length === 0 && (
            <Alert variant="warning" className="mt-3 mb-0 small">
              Sin ninguna herramienta encendida el asistente se conecta y no
              puede consultar nada.
            </Alert>
          )}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mb-4">
        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? "Guardando…" : "Guardar configuración"}
        </Button>
      </div>

      {usaLlaves ? (
        <McpCredentials />
      ) : (
        <Alert variant="secondary">
          En modo OAuth las llaves no se usan: quien reparte y retira accesos es
          el servidor de autorización. Las que estén emitidas se quedan
          guardadas por si vuelves a ese modo.
        </Alert>
      )}
    </div>
  );
};

export default McpAdmin;
